import React, { useState, useMemo } from 'react'

const TimezoneSelector = ({ value = 'Asia/Kolkata', onChange }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Common timezones grouped by region
  const timezones = [
    // IST (India) - Default timezone
    { value: 'Asia/Kolkata', label: 'India Standard Time (IST)', region: 'Asia', offset: '+05:30' },
    
    // UTC and Common
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)', region: 'UTC', offset: '+00:00' },
    
    // North America
    { value: 'America/New_York', label: 'Eastern Time (New York)', region: 'North America', offset: '-05:00' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)', region: 'North America', offset: '-06:00' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)', region: 'North America', offset: '-07:00' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)', region: 'North America', offset: '-08:00' },
    { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)', region: 'North America', offset: '-09:00' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (Honolulu)', region: 'North America', offset: '-10:00' },
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)', region: 'North America', offset: '-05:00' },
    { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)', region: 'North America', offset: '-08:00' },
    
    // Europe
    { value: 'Europe/London', label: 'Greenwich Mean Time (London)', region: 'Europe', offset: '+00:00' },
    { value: 'Europe/Paris', label: 'Central European Time (Paris)', region: 'Europe', offset: '+01:00' },
    { value: 'Europe/Berlin', label: 'Central European Time (Berlin)', region: 'Europe', offset: '+01:00' },
    { value: 'Europe/Rome', label: 'Central European Time (Rome)', region: 'Europe', offset: '+01:00' },
    { value: 'Europe/Madrid', label: 'Central European Time (Madrid)', region: 'Europe', offset: '+01:00' },
    { value: 'Europe/Amsterdam', label: 'Central European Time (Amsterdam)', region: 'Europe', offset: '+01:00' },
    { value: 'Europe/Stockholm', label: 'Central European Time (Stockholm)', region: 'Europe', offset: '+01:00' },
    { value: 'Europe/Moscow', label: 'Moscow Standard Time', region: 'Europe', offset: '+03:00' },
    
    // Asia
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)', region: 'Asia', offset: '+09:00' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)', region: 'Asia', offset: '+08:00' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong Time', region: 'Asia', offset: '+08:00' },
    { value: 'Asia/Singapore', label: 'Singapore Standard Time', region: 'Asia', offset: '+08:00' },
    { value: 'Asia/Seoul', label: 'Korea Standard Time (Seoul)', region: 'Asia', offset: '+09:00' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)', region: 'Asia', offset: '+04:00' },
    { value: 'Asia/Bangkok', label: 'Indochina Time (Bangkok)', region: 'Asia', offset: '+07:00' },
    
    // Australia & Pacific
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)', region: 'Australia', offset: '+10:00' },
    { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)', region: 'Australia', offset: '+10:00' },
    { value: 'Australia/Perth', label: 'Australian Western Time (Perth)', region: 'Australia', offset: '+08:00' },
    { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (Auckland)', region: 'Pacific', offset: '+12:00' },
    
    // South America
    { value: 'America/Sao_Paulo', label: 'Brasília Time (São Paulo)', region: 'South America', offset: '-03:00' },
    { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time (Buenos Aires)', region: 'South America', offset: '-03:00' },
    { value: 'America/Lima', label: 'Peru Time (Lima)', region: 'South America', offset: '-05:00' },
    
    // Africa
    { value: 'Africa/Cairo', label: 'Eastern European Time (Cairo)', region: 'Africa', offset: '+02:00' },
    { value: 'Africa/Johannesburg', label: 'South Africa Standard Time', region: 'Africa', offset: '+02:00' },
    { value: 'Africa/Lagos', label: 'West Africa Time (Lagos)', region: 'Africa', offset: '+01:00' }
  ]

  // Filter timezones based on search term
  const filteredTimezones = useMemo(() => {
    if (!searchTerm) return timezones
    
    const term = searchTerm.toLowerCase()
    return timezones.filter(tz => 
      tz.label.toLowerCase().includes(term) ||
      tz.value.toLowerCase().includes(term) ||
      tz.region.toLowerCase().includes(term)
    )
  }, [searchTerm])

  // Group filtered timezones by region
  const groupedTimezones = useMemo(() => {
    const groups = {}
    filteredTimezones.forEach(tz => {
      if (!groups[tz.region]) {
        groups[tz.region] = []
      }
      groups[tz.region].push(tz)
    })
    return groups
  }, [filteredTimezones])

  const selectedTimezone = timezones.find(tz => tz.value === value)

  const handleSelect = (timezoneValue) => {
    onChange(timezoneValue)
    setIsOpen(false)
    setSearchTerm('')
  }

  return (
    <div className="timezone-selector relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Timezone
      </label>
      
      {/* Selected timezone display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
      >
        <div className="flex items-center">
          <span className="text-sm text-gray-600 mr-2">🌍</span>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {selectedTimezone?.label || value}
            </div>
            <div className="text-xs text-gray-500">
              {selectedTimezone?.offset || 'Unknown offset'}
            </div>
          </div>
        </div>
        
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Search input */}
          <div className="p-3 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search timezones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Timezone list */}
          <div className="max-h-80 overflow-y-auto">
            {Object.keys(groupedTimezones).length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No timezones found matching "{searchTerm}"
              </div>
            ) : (
              Object.entries(groupedTimezones).map(([region, regionTimezones]) => (
                <div key={region}>
                  {/* Region header */}
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      {region}
                    </h4>
                  </div>
                  
                  {/* Timezones in region */}
                  {regionTimezones.map((timezone) => (
                    <button
                      key={timezone.value}
                      type="button"
                      onClick={() => handleSelect(timezone.value)}
                      className={`w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors ${
                        value === timezone.value ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium">
                            {timezone.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {timezone.value}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 ml-2">
                          {timezone.offset}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Help text */}
      <div className="mt-2 text-xs text-gray-500">
        💡 The timezone affects when occasion dates are evaluated. Choose the timezone most relevant to your audience.
      </div>
    </div>
  )
}

export { TimezoneSelector }
